import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight, Send, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../../lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setLoading(true);
      await subscribeNewsletter(email.trim());
      toast.success("You're on the list. Welcome to the lab.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-ink text-[#FAFAFA] mt-32">
      <div className="container-wide py-20 md:py-28">
        <div className="mb-14 md:mb-20 flex items-center justify-between gap-8 border-b border-white/15 pb-10">
          <Link to="/" data-testid="footer-logo" className="inline-flex items-center bg-white p-3 md:p-4 border border-white/20">
            <img
              src="https://customer-assets.emergentagent.com/job_deployedd-labs/artifacts/glgihy7l_Deployed%20Logo-01.png"
              alt="Deployedd Labs"
              width="80"
              height="80"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>
          <p className="hidden md:block font-mono-cust text-[11px] uppercase tracking-[0.3em] text-white/40 max-w-xs text-right">
            Knowledge · Innovation · Acceleration
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
              The Lab Dispatch — Quarterly
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-8 max-w-[16ch]">
              Field notes from the <em className="font-editorial italic text-[#E68A6E]">edge of work.</em>
            </h2>
            <form onSubmit={onSubmit} data-testid="footer-newsletter-form" className="flex items-end gap-4 max-w-xl border-b border-white/30 focus-within:border-[#E68A6E] transition-colors">
              <input
                data-testid="footer-newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent py-4 text-lg outline-none placeholder:text-white/40"
              />
              <button
                data-testid="footer-newsletter-submit"
                type="submit"
                disabled={loading}
                className="pb-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] hover:text-[#E68A6E] transition-colors disabled:opacity-50"
              >
                {loading ? "Sending" : "Subscribe"} <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 md:grid-cols-3 gap-10">
            <div>
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4">Ecosystem</p>
              <ul className="space-y-3">
                <li><Link to="/services" data-testid="footer-link-services" className="link-underline text-white/85 hover:text-white">Capability Building</Link></li>
                <li><Link to="/services" className="link-underline text-white/85 hover:text-white">Talent Deployment</Link></li>
                <li><Link to="/saas" className="link-underline text-white/85 hover:text-white">Deployedd Digital</Link></li>
                <li><Link to="/services" className="link-underline text-white/85 hover:text-white">Sustainability & EQ</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4">Company</p>
              <ul className="space-y-3">
                <li><Link to="/about" className="link-underline text-white/85 hover:text-white">About</Link></li>
                <li><Link to="/pulse" className="link-underline text-white/85 hover:text-white">Pulse</Link></li>
                <li><Link to="/blog" className="link-underline text-white/85 hover:text-white">Journal</Link></li>
                <li><Link to="/contact" className="link-underline text-white/85 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4">Connect</p>
              <ul className="space-y-3">
                <li><a href="https://wa.me/918122431120" data-testid="footer-whatsapp" target="_blank" rel="noopener noreferrer" className="link-underline inline-flex items-center gap-1 text-white/85 hover:text-white">+91 81224 31120 · WhatsApp <ArrowUpRight className="w-3.5 h-3.5" /></a></li>
                <li><a href="mailto:contact@deployedd.com" data-testid="footer-email" className="link-underline text-white/85 hover:text-white">contact@deployedd.com</a></li>
                <li><a href="https://www.linkedin.com/company/deployedd-labs/" data-testid="footer-linkedin" target="_blank" rel="noopener noreferrer" className="link-underline inline-flex items-center gap-2 text-white/85 hover:text-white">
                  <span className="inline-flex items-center justify-center w-7 h-7 border border-white/30 hover:border-[#E68A6E] hover:text-[#E68A6E] transition-colors">
                    <Linkedin className="w-3.5 h-3.5" />
                  </span>
                  LinkedIn
                </a></li>
                <li><a href="#" className="link-underline text-white/85 hover:text-white">YouTube</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/50">
          <p className="font-mono-cust uppercase tracking-[0.25em]">© {new Date().getFullYear()} Deployedd Labs — Knowledge · Innovation · Acceleration</p>
          <p className="font-mono-cust uppercase tracking-[0.25em]">Chennai · Coimbatore</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
